import React from 'react'
import { motion } from 'framer-motion'
import { 
  ShoppingBag, Search, Filter, Plus, Star, Grid, List, 
  SortAsc as Sort, MapPin, Clock, Heart, Eye, Shield, Award 
} from 'lucide-react'

const MarketplacePage = () => {
  return (
    <div className="pt-16 lg:pt-20 min-h-screen page-transition">
      {/* Header */}
      <section className="section-padding py-16 bg-gradient-to-b from-le-darker/50 to-transparent">
        <div className="container-max">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl lg:text-7xl font-display font-bold text-white mb-6">
              Gear
              <span className="text-gradient block">Marketplace</span>
            </h1>
            <p className="text-2xl text-white/70 max-w-4xl mx-auto mb-8">
              Buy and sell music equipment with trusted local musicians and audio professionals. 
              Secure transactions, verified sellers, and quality gear.
            </p>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              {[
                { label: 'Active Listings', value: '200+', icon: ShoppingBag, color: 'text-le-accent' },
                { label: 'Verified Sellers', value: '85%', icon: Shield, color: 'text-green-400' },
                { label: 'Avg Response Time', value: '< 2hrs', icon: Clock, color: 'text-le-accent-2' },
                { label: 'Customer Rating', value: '4.8★', icon: Award, color: 'text-yellow-400' }
              ].map((stat, index) => {
                const Icon = stat.icon
                return (
                  <motion.div
                    key={stat.label}
                    className="glass-card p-6 text-center card-hover"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Icon size={32} className={`${stat.color} mx-auto mb-3`} />
                    <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-sm text-white/60">{stat.label}</div>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Coming Soon Content */}
      <section className="section-padding pb-24">
        <div className="container-max">
          <motion.div
            className="glass-card p-16 text-center relative overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-le-accent/10 via-transparent to-le-accent-2/10"></div>
            <div className="relative z-10">
              <ShoppingBag size={80} className="text-le-accent mx-auto mb-8 animate-pulse-glow" />
              <h2 className="text-4xl font-display font-bold text-white mb-6">
                Marketplace Launching Soon
              </h2>
              <p className="text-xl text-white/70 mb-8 max-w-3xl mx-auto leading-relaxed">
                We're building the most trusted marketplace for music equipment in Kansas. 
                Secure payments, verified sellers, detailed condition reports, and local pickup options.
              </p>
              
              {/* Feature Preview Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                {[
                  {
                    icon: Shield,
                    title: 'Escrow Protection',
                    description: 'Your money held safely until you receive and approve your gear'
                  },
                  {
                    icon: Star,
                    title: 'Seller Ratings',
                    description: 'Comprehensive feedback system with verified purchase history'
                  },
                  {
                    icon: MapPin,
                    title: 'Local Priority',
                    description: 'Find gear near you first, support local musicians'
                  }
                ].map((feature, index) => {
                  const Icon = feature.icon
                  return (
                    <motion.div
                      key={feature.title}
                      className="glass-card p-6 hover:bg-white/10 transition-all duration-300"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                    >
                      <Icon size={40} className="text-le-accent-2 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-white mb-3">{feature.title}</h3>
                      <p className="text-white/70 text-sm leading-relaxed">{feature.description}</p>
                    </motion.div>
                  )
                })}
              </div>

              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <motion.button 
                  className="btn-primary text-xl px-12 py-6"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get Notified at Launch
                </motion.button>
                <motion.button 
                  className="btn-secondary text-xl px-12 py-6"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Learn More
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default MarketplacePage